import { FC, MouseEvent, useState } from 'react'
import { useRouter } from 'next/router'
import { Button, FlexboxGrid, Panel, Tag } from 'rsuite'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import Link from 'next/link'

import { Product, SupplierMappedProductsProps } from '../../../utils/types/Product'
import { getEllipsesText } from '../../../utils/util'
import SupplierOrderInfoSection from '../../SupplierOrderInfoSection'
import { Country, CountryId, ScreenSize } from '../../../utils/constants'
import useUrls from '../../../services/useUrls'
import { getSmallPreview } from '../../../utils/images'
import { getSupplierName, getSupplierShopPageUrl, isNewSupplier } from '../../../lib/supplier'
import useIsOnMobile from '../../../services/useIsOnMobile'
import Avatar from '../../Avatar/Avatar'
import { getTextContentByType } from '../../../lib/text'
import { getSupplierDeliveryDetails } from '../../../lib/service'
import { getCountryAndLocaleStrings } from '../../../utils/locales'

import styles from '../../../styles/SupplierCarousel.module.less'
import tagStyles from '../../../styles/Tag.module.less'

const DynamicSupplierContactModal = dynamic(() => import('../../Modals/SupplierContactModal'))

interface SupplierCardProps {
  supplierWithProducts: SupplierMappedProductsProps
  products: Product[]
  onClick?: (event: MouseEvent<Element>) => void
}

const SupplierCard: FC<SupplierCardProps> = (props) => {
  const { supplierWithProducts, products, onClick = () => {} } = props

  const [isSupplierContactModalOpen, setIsSupplierContactModalOpen] = useState(false)
  const { t } = useTranslation('products')
  const { pushT, urlT } = useUrls()
  const { locale: countryAndLocale } = useRouter()
  const { country } = getCountryAndLocaleStrings(countryAndLocale)
  const isOnMobile = useIsOnMobile(ScreenSize.md)
  const isOnSM = useIsOnMobile(ScreenSize.sm)
  const supplierCode = supplierWithProducts['supplier.code']
  const countryId = supplierWithProducts['supplier.address']?.[0]?.['supplier.address.countryid']?.toLocaleLowerCase()
  const supplierPageRedirectUrl = getSupplierShopPageUrl(supplierCode, urlT)

  const onSendInquiryClick = () => setIsSupplierContactModalOpen(true)

  const supplierDescriptionLength = isOnSM ? 50 : 140

  const supplierDescription = getEllipsesText(getTextContentByType(supplierWithProducts, 'long') || '', supplierDescriptionLength)
  const supplierName = getSupplierName(supplierWithProducts)
  const supplierLogoAltDescription = t('meta:supplierLogoAltDescription', { supplierName })
  const { deliveryTime } = getSupplierDeliveryDetails(supplierWithProducts, country)

  return (
    <div className={styles['supplier-card']}>
      {isSupplierContactModalOpen && (
        <DynamicSupplierContactModal
          open={isSupplierContactModalOpen}
          setOpen={setIsSupplierContactModalOpen}
          supplier={supplierWithProducts}
          analyticsLocation="supplier_card"
        />
      )}
      <Panel
        bordered
        bodyFill
        onClick={(event: MouseEvent<Element>) => {
          if (isOnMobile) {
            pushT(supplierPageRedirectUrl)
            onClick(event)
          }
        }}
      >
        <div className={styles['supplier-section']}>
          <FlexboxGrid
            align="middle"
            className={styles['supplier-row-gap']}
          >
            <FlexboxGrid.Item>
              <div
                className={styles['supplier-logo']}
                data-testid="supplier-avatar"
              >
                <Link
                  href={supplierPageRedirectUrl}
                  onClick={onClick}
                >
                  <Avatar
                    size="xl"
                    imageSrc={(getSmallPreview(supplierWithProducts?.media?.[0]))}
                    altName={supplierLogoAltDescription}
                  />
                </Link>
              </div>
            </FlexboxGrid.Item>
            <FlexboxGrid.Item
              className={styles['supplier-label']}
              data-testid="supplier-card-link"
            >
              <Link
                href={supplierPageRedirectUrl}
                onClick={onClick}
              >
                <h2
                  data-testid="supplier-card-heading"
                  className="ellipsis"
                >
                  {supplierWithProducts['supplier.label']}
                </h2>
              </Link>
              {countryId && (
                <div className="margin-bottom-spacer-half">
                  {`${t('Location')}: ${t(
                    `countries:${Country[countryId as CountryId]}`,
                  )}`}
                </div>
              )}
              {deliveryTime && (
                <p>
                  {t('Delivery time {{ range }} business days', { range: deliveryTime })}
                </p>
              )}
              {isNewSupplier(supplierWithProducts) && (
                <Tag
                  color="primary"
                  className={`${tagStyles.tag} tag-primary margin-top-spacer`}
                >
                  {t('New Supplier')}
                </Tag>
              )}
            </FlexboxGrid.Item>
          </FlexboxGrid>
          <div className="margin-top-spacer-double">
            {supplierDescription}
          </div>
          <div className="margin-top-spacer-double">
            <SupplierOrderInfoSection
              products={products}
              supplier={supplierWithProducts}
            />
          </div>
          {!isOnMobile && (
            <>
              <Link href={supplierPageRedirectUrl}>
                <Button
                  onClick={onClick}
                  appearance="ghost"
                  className="margin-top-spacer-double margin-right-spacer"
                >
                  {t('Browse all products')}
                </Button>
              </Link>
              <Button
                appearance="link"
                className={`${styles['dark-text-button']} margin-top-spacer-double`}
                onClick={onSendInquiryClick}
              >
                {t('Send inquiry')}
              </Button>
            </>
          )}
        </div>
      </Panel>
    </div>
  )
}

export default SupplierCard
